import { alpha, keyframes, styled } from '@mui/material';
import { FONT_DISPLAY } from 'themes/tokens';

const fade = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const pop = keyframes`from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); }`;

export const Background = styled('div')(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: theme.zIndex.modal - 50,
  display: 'grid',
  placeItems: 'center',
  padding: 16,
  backgroundColor: 'rgba(3, 6, 12, 0.72)',
  backdropFilter: 'blur(3px)',
  WebkitBackdropFilter: 'blur(3px)',
  animation: `${fade} 0.16s ease`,
}));

export const Panel = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  width: 'min(1200px, 94vw)',
  height: 'min(88vh, 860px)',
  backgroundColor: theme.palette.background.paper,
  backgroundImage: 'none',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 18,
  boxShadow: '0 40px 100px -24px rgba(0, 0, 0, 0.85)',
  overflow: 'hidden',
  outline: 'none',
  animation: `${pop} 0.18s ease`,
}));

export const SectionList = styled('nav')(({ theme }) => ({
  width: 260,
  flex: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: theme.spacing(2, 1.5),
  backgroundColor: theme.palette.background.default,
  borderRight: `1px solid ${theme.palette.divider}`,
  overflowY: 'auto',
}));

export const SectionListItem = styled('button', {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  width: '100%',
  padding: theme.spacing(1.25, 1.5),
  border: 'none',
  borderRadius: 10,
  font: 'inherit',
  fontSize: '0.875rem',
  fontWeight: 600,
  textAlign: 'left',
  cursor: 'pointer',
  color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.16) : 'transparent',
  transition: theme.transitions.create(['background-color', 'color']),
  '& svg': { flexShrink: 0, color: selected ? theme.palette.primary.light : 'inherit' },
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: alpha(theme.palette.primary.main, selected ? 0.22 : 0.08),
  },
  ...(selected && {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '22%',
      bottom: '22%',
      width: 3,
      borderRadius: '0 3px 3px 0',
      backgroundColor: theme.palette.accent.main,
      boxShadow: `0 0 10px ${alpha(theme.palette.accent.main, 0.6)}`,
    },
  }),
}));

export const RightPane = styled('div')({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const PaneHeader = styled('header')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  flex: 'none',
  padding: theme.spacing(1.5, 2, 1.5, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const PaneTitle = styled('h2')(({ theme }) => ({
  margin: 0,
  fontFamily: FONT_DISPLAY,
  fontSize: '1.25rem',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  color: theme.palette.text.primary,
}));

export const PaneBody = styled('div')(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: theme.spacing(3),
  isolation: 'isolate',
}));
